import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import api from "@/services/api";
import AuthPromptModal from "@/components/Modals/AuthPromptModal";
import ExperienciaCard from "@/components/ExperienciaCard";
import TicketCarousel from "@/components/TicketCarousel/TicketCarousel";
import { scrollToTop } from "@/lib/utilsUI";
import {
  agregarExperienciaFavorita,
  eliminarExperienciaFavorita,
  getFavoritasExperiencias,
} from "@/features/eventosFavoritos/eventosFavoritosSlice";

const DOMAIN = import.meta.env.VITE_DOMAIN || "https://appwhoop.com";

function updateMetaTags({ title, description, image, url }) {
  document.title = title || "AppWhoop";

  const setMeta = (name, content, prop = false) => {
    const selector = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
    let el = document.querySelector(selector);
    if (!el) {
      el = document.createElement("meta");
      prop ? el.setAttribute("property", name) : el.setAttribute("name", name);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content || "");
  };

  setMeta("description", description);
  setMeta("og:title", title, true);
  setMeta("og:description", description, true);
  setMeta("og:image", image, true);
  setMeta("og:url", url, true);
  setMeta("og:type", "article", true);

  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", url);
}

function injectJsonLd(experiencia) {
  const id = `jsonld-experiencia-${experiencia.slug}`;
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: experiencia.metaTitulo || experiencia.titulo,
    description: experiencia.metaDescripcion || experiencia.resumen,
    image: experiencia.imagenPrincipal,
    datePublished: experiencia.createdAt,
    dateModified: experiencia.updateAt || experiencia.createdAt,
    url: `${DOMAIN}/experiencias/${experiencia.slug}`,
    publisher: {
      "@type": "Organization",
      name: "AppWhoop",
      url: DOMAIN,
    },
  });
}

export default function ExperienciaDetalle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { favoritasExperiencias } = useSelector((state) => state.eventosFavoritos);

  const [experiencia, setExperiencia] = useState(null);
  const [relacionadas, setRelacionadas] = useState([]);
  const [relacionadasPage, setRelacionadasPage] = useState(1);
  const [relacionadasHasMore, setRelacionadasHasMore] = useState(false);
  const [relacionadasLoadingMore, setRelacionadasLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareStatus, setShareStatus] = useState(null);
  const [authPromptOpen, setAuthPromptOpen] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  const isFavorite = favoritasExperiencias.some((e) => e.slug === slug);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getFavoritasExperiencias());
    }
  }, [isAuthenticated]);

  useEffect(() => {
    scrollToTop();
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get(`/experiencias/${slug}`);
        const exp = data.data;
        setExperiencia(exp);

        // SEO tags
        updateMetaTags({
          title: exp.metaTitulo || exp.titulo,
          description: exp.metaDescripcion || exp.resumen,
          image: exp.imagenPrincipal,
          url: `${DOMAIN}/experiencias/${exp.slug}`,
        });
        injectJsonLd(exp);

        // Cargar relacionadas
        if (exp.categoria) {
          const rel = await api.get(
            `/experiencias?categoria=${encodeURIComponent(exp.categoria)}&page=1&limit=5`,
          );
          const items = (rel.data.data || [])
            .filter((r) => r.slug !== slug)
            .map((r) => ({ ...r, uid: `experiencia-${r.id}` }));
          setRelacionadas(items);
          setRelacionadasPage(1);
          setRelacionadasHasMore(rel.data.pagination?.hasNextPage || false);
        }
      } catch (err) {
        if (err.response?.status === 404) {
          navigate("/404", { replace: true });
        } else {
          setError("No se pudo cargar la experiencia. Intenta recargar.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    return () => {
      // Limpiar JSON-LD al desmontar
      const script = document.getElementById(`jsonld-experiencia-${slug}`);
      if (script) script.remove();
    };
  }, [slug]);

  const handleShare = async () => {
    const shareData = {
      title: experiencia?.titulo,
      text: experiencia?.resumen,
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setShareStatus("¡Link copiado!");
        setTimeout(() => setShareStatus(null), 2500);
      }
    } catch {
      // El usuario canceló el share — ignorar
    }
  };

  const handleVisitarLugar = (e) => {
    if (!isAuthenticated) {
      e.preventDefault();
      setAuthPromptOpen(true);
    }
  };

  const handleFavoriteToggle = async () => {
    if (!isAuthenticated) {
      setAuthPromptOpen(true);
      return;
    }
    setFavoriteLoading(true);
    try {
      if (isFavorite) {
        await dispatch(eliminarExperienciaFavorita(slug)).unwrap();
      } else {
        await dispatch(
          agregarExperienciaFavorita({
            slug,
            titulo: experiencia?.titulo,
            imagen: experiencia?.imagenPrincipal,
          })
        ).unwrap();
      }
    } catch (error) {
      console.error("Error al cambiar favorito de experiencia:", error);
    } finally {
      setFavoriteLoading(false);
    }
  };

  const handleLoadMoreRelacionadas = async () => {
    if (relacionadasLoadingMore || !relacionadasHasMore || !experiencia?.categoria) return;
    setRelacionadasLoadingMore(true);
    try {
      const nextPage = relacionadasPage + 1;
      const rel = await api.get(
        `/experiencias?categoria=${encodeURIComponent(experiencia.categoria)}&page=${nextPage}&limit=5`,
      );
      const items = (rel.data.data || [])
        .filter((r) => r.slug !== slug)
        .map((r) => ({ ...r, uid: `experiencia-${r.id}` }));
      setRelacionadas((prev) => [...prev, ...items]);
      setRelacionadasPage(nextPage);
      setRelacionadasHasMore(rel.data.pagination?.hasNextPage || false);
    } catch (err) {
      console.error("Error cargando más relacionadas:", err);
    } finally {
      setRelacionadasLoadingMore(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-aw-bgbluedark-30">
        <FontAwesomeIcon
          icon="fa-solid fa-spinner"
          className="animate-spin text-aw-green text-4xl"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-aw-bgbluedark-30 gap-4">
        <p className="text-red-400">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="bg-aw-green text-black px-4 py-2 rounded font-semibold"
        >
          Recargar
        </button>
      </div>
    );
  }

  if (!experiencia) return null;

  return (
    <div className="min-h-screen bg-aw-bgbluedark-30 text-white">
      <div className="max-w-6xl mx-auto px-2 py-8">
        {/* ── Columna izquierda (sticky) + columna derecha (artículo) ── */}
        <div className="md:grid md:grid-cols-[2fr_3fr] md:gap-10 md:items-start mb-8">
          {/* Izquierda — imagen + título + resumen + botones (sticky) */}
          <div className="min-w-0 mb-8 md:mb-0 md:sticky md:top-8">
            <div className="w-full aspect-square overflow-hidden relative rounded-2xl mb-5">
              <img
                src={experiencia.imagenPrincipal}
                alt={experiencia.titulo}
                className="w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              {experiencia.categoria && (
                <span className="absolute top-4 left-4 bg-aw-green text-aw-bluedark text-xs font-bold px-3 py-1 rounded-full">
                  {experiencia.categoria}
                </span>
              )}
            </div>

            <h1 className="text-2xl md:text-3xl font-bold leading-tight mb-3">
              {experiencia.titulo}
            </h1>
            <p className="text-gray-300 text-base mb-5 leading-relaxed">
              {experiencia.resumen}
            </p>

            {/* Botones */}
            <div className="flex flex-wrap gap-3">
              {experiencia.enlaceExterno && (
                <a
                  href={experiencia.enlaceExterno}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleVisitarLugar}
                  className="inline-flex items-center gap-2 bg-aw-green text-black font-semibold px-5 py-2.5 rounded-lg hover:scale-105 transition duration-200"
                >
                  <FontAwesomeIcon icon="fa-solid fa-arrow-up-right-from-square" />
                  {experiencia.botonTexto || 'Registrate'}
                </a>
              )}
              {experiencia.enlaceInterno && (
                <Link
                  to={experiencia.enlaceInterno}
                  onClick={scrollToTop}
                  className="inline-flex items-center gap-2 bg-white text-aw-bluedark font-semibold px-5 py-2.5 rounded-lg hover:scale-105 transition duration-200"
                >
                  <FontAwesomeIcon icon="fa-solid fa-ticket" />
                  Comprar ticket
                </Link>
              )}
              <button
                onClick={handleShare}
                className="inline-flex items-center gap-2 bg-aw-bluedark text-white font-medium px-5 py-2.5 rounded-lg border border-gray-600 hover:border-aw-green transition duration-200"
              >
                <FontAwesomeIcon icon="fa-solid fa-share-nodes" />
                {shareStatus || "Compartir"}
              </button>
              <button
                onClick={handleFavoriteToggle}
                disabled={favoriteLoading}
                title={isFavorite ? "Quitar de favoritos" : "Agregar a favoritos"}
                className={`inline-flex items-center gap-2 font-medium px-5 py-2.5 rounded-lg border transition duration-200 ${
                  isFavorite
                    ? "bg-red-500/20 border-red-500 text-red-400 hover:bg-red-500/30"
                    : "bg-aw-bluedark text-white border-gray-600 hover:border-aw-green"
                } ${favoriteLoading ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FontAwesomeIcon
                  icon={
                    favoriteLoading
                      ? "fa-solid fa-spinner"
                      : isFavorite
                      ? "fa-solid fa-heart"
                      : "fa-regular fa-heart"
                  }
                  className={favoriteLoading ? "animate-spin" : ""}
                />
                {isFavorite ? "Guardado" : "Guardar"}
              </button>
            </div>
          </div>

          {/* Derecha — solo el artículo y CTA */}
          <div className="min-w-0">
            <article
              className="prose prose-invert prose-lg max-w-none mb-10 overflow-x-auto
                prose-headings:text-white prose-p:text-gray-300
                prose-a:text-aw-green prose-strong:text-white
                prose-img:rounded-xl prose-img:w-full"
              dangerouslySetInnerHTML={{ __html: experiencia.contenido }}
            />
          </div>
        </div>

        <div className="bg-aw-bluedark rounded-2xl p-6 flex flex-col sm:flex-row items-center gap-4 border border-aw-green/30">
          <div className="flex-1">
            <h3 className="text-lg font-bold mb-1">¿Te gustó este Panorama?</h3>
            <p className="text-gray-400 text-sm">
              Descubre los mejores Eventos y Panoramas en AppWhoop.com Regístrate Gratis para obtener Invitaciones, Descuentos y Tickets Digitales.
            </p>
          </div>
          {!isAuthenticated && (
            <Link
              to="/signup"
              onClick={scrollToTop}
              className="shrink-0 bg-aw-green text-black font-bold px-6 py-3 rounded-xl hover:scale-105 transition duration-200"
            >
              Únete gratis
            </Link>
          )}
        </div>

        {/* ── Más panoramas — ancho completo, debajo de todo ── */}
        {relacionadas.length > 0 && (
          <>
            <h2 className="text-xl font-semibold mt-12 mb-3">Más panoramas</h2>
            <TicketCarousel
              tickets={relacionadas}
              TicketItem={ExperienciaCard}
              hasMore={relacionadasHasMore}
              isLoadingMore={relacionadasLoadingMore}
              onLoadMore={handleLoadMoreRelacionadas}
            />
          </>
        )}
      </div>

      <AuthPromptModal
        isOpen={authPromptOpen}
        onClose={() => setAuthPromptOpen(false)}
        returnTo={`/experiencias/${slug}`}
      />
    </div>
  );
}
